'use client'

import type { MeetingDay } from '@repo/core'
import { Building2, type LucideIcon, Mail, MapPin, Monitor, Users } from 'lucide-react'
import { type ChangeEvent, useEffect, useState } from 'react'

import { Reveal } from '@/components/atoms/reveal.client'
import { AUDIENCE_STEPS, AudienceSlider } from '@/components/molecules/audience-slider.client'
import {
  type BookingSlot,
  ConsultationCalendar,
} from '@/components/molecules/consultation-calendar.client'
import { CurvedInput } from '@/components/molecules/curved-input.client'
import { Step, Stepper } from '@/components/molecules/stepper.client'
import { useToast } from '@/components/providers/ToastProvider'
import type { Dictionary } from '@/lib/home-copy'
import { CARD_TITLE } from '@/lib/typography'
import { cn } from '@/lib/utils'
import { submitLeadAction } from '@/server/leads'
import { bookMeetingAction, meetingDaysAction } from '@/server/meetings'

type HomeContactStepperProps = {
  copy: Dictionary['Public']['home']['contactStepper']
  id?: string
  /**
   * When a meeting could be taken, as the page was rendered.
   *
   * Passed in rather than fetched here so the first paint has a calendar: the
   * page is prerendered and the picker would otherwise draw an empty month
   * until a round trip came back. It is refreshed on mount all the same — see
   * the effect in the component — because a cached page can be five minutes
   * behind the calendar.
   */
  meetingDays?: readonly MeetingDay[]
}

const LINK_REGEX = /^(https?:\/\/)?([\w-]+\.)+[\w-]{2,}(\/\S*)?$/i

type Profile = 'firma' | 'tworca'

/**
 * How somebody wants to reach us — asked SECOND, right after who they are.
 *
 * It used to be asked last, and only in one flavour: the final step demanded a
 * format AND a calendar slot, so the form had exactly one ending — a booked
 * consultation. Somebody who only wanted to write a message had to book a
 * meeting to send it, and the most common intent on a contact page was the one
 * intent the page refused.
 *
 * Asking early also shortens the path it opens: a message needs no calendar, so
 * that step is not rendered at all rather than shown and skipped.
 */
type Channel = 'mail' | 'online' | 'stacjonarnie'

/** A meeting has a calendar; a message does not. The one branch that matters. */
function isMeeting(channel: Channel | null): boolean {
  return channel === 'online' || channel === 'stacjonarnie'
}

type ContactFormState = {
  profile: Profile | null
  /** What a MESSAGE is about. Null on the meeting branch, which never asks. */
  matter: string | null
  /** Index into AUDIENCE_STEPS. Only meaningful when profile is "tworca". */
  audience: number
  channel: Channel | null
  focus: string[]
  details: string
  name: string
  email: string
  link: string
  slot: BookingSlot
}

const initialState: ContactFormState = {
  profile: null,
  matter: null,
  audience: 0,
  channel: null,
  focus: [],
  details: '',
  name: '',
  email: '',
  link: '',
  slot: null,
}

/**
 * The e-mail rule, and the two sentences it can produce.
 *
 * Deliberately not a full RFC 5322 regex: this runs while somebody is still
 * typing, and its whole job is to say "that is missing an @" before they press
 * the button. The server's schema is the one that refuses the address.
 */
function emailProblem(
  email: string,
  copy: { emailRequired: string; emailInvalid: string },
): string | undefined {
  if (email.length === 0) return copy.emailRequired
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email) ? undefined : copy.emailInvalid
}

/**
 * One pickable tile — used by every choice on this form.
 *
 * ── WHY IT LOOKS LIKE A PRICING CARD ─────────────────────────────────────
 * Because it is the same kind of object, and the site already had a treatment
 * for it. The tiles here used to be flat: near-black on near-black, and the
 * picked one turned solid white. Next to `/pl/montaz`, where the chosen plan
 * carries the brand gradient on its border and a glow under it, the contact
 * page read as the unfinished corner of the site.
 *
 * The selected state is lifted verbatim from that card — gradient ring painted
 * on `border-box` over a `padding-box` fill, so the glass survives — which is
 * what makes "picked" read as picked rather than as inverted.
 */
function ChoiceTile({
  label,
  hint,
  Icon,
  tone,
  active,
  onClick,
}: {
  label: string
  hint?: string
  Icon?: LucideIcon
  /**
   * One stop on the brand ramp, for tiles that are three different THINGS
   * rather than three of a kind.
   *
   * The three ways of reaching us are the clearest case: identical grey boxes
   * made "write" and "come and see us" look like the same decision. Omitted
   * everywhere the options really are a list — the eight kinds of matter, the
   * five service areas — because colouring those would be decoration.
   */
  tone?: string
  active: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'flex flex-col gap-2 rounded-2xl border p-[clamp(0.7rem,1.5svh,1rem)] text-left transition-colors duration-300',
        active
          ? 'border-transparent [background:linear-gradient(oklch(0.185_0.03_300),oklch(0.16_0.025_300))_padding-box,var(--flou-gradient)_border-box] shadow-[0_28px_90px_-40px_var(--flou-glow-core)]'
          : 'border-white/10 bg-white/[0.03] hover:border-white/25 hover:bg-white/[0.06]',
      )}
    >
      {Icon ? (
        <span
          className={cn(
            'flex h-9 w-9 items-center justify-center rounded-full transition-colors duration-300',
            active ? 'text-white' : 'text-white/70',
          )}
          style={
            tone
              ? {
                  backgroundColor: active ? tone : `color-mix(in oklab, ${tone} 22%, transparent)`,
                  boxShadow: active ? `0 10px 26px -12px ${tone}` : undefined,
                }
              : { backgroundColor: 'rgba(255,255,255,0.06)' }
          }
        >
          <Icon size={16} aria-hidden="true" />
        </span>
      ) : null}
      <span className={cn('text-sm font-bold', active ? 'text-white' : 'text-white/85')}>
        {label}
      </span>
      {hint ? (
        <span
          className={cn('text-[0.8rem] leading-snug', active ? 'text-white/70' : 'text-white/50')}
        >
          {hint}
        </span>
      ) : null}
    </button>
  )
}

/** The heading pair every step opens with. */
function StepHead({ title, hint }: { title: string; hint: string }) {
  return (
    <div>
      <h2 className={cn(CARD_TITLE, 'text-white')}>{title}</h2>
      <p className="mt-1 text-sm text-white/55">{hint}</p>
    </div>
  )
}

export function HomeContactStepper({ copy, id, meetingDays = [] }: HomeContactStepperProps) {
  const [formState, setFormState] = useState<ContactFormState>(initialState)
  const [submitted, setSubmitted] = useState(false)
  const [activeStep, setActiveStep] = useState(1)
  const [sending, setSending] = useState(false)
  const { toast } = useToast()

  /**
   * The calendar, as it stands NOW rather than as it was cached.
   *
   * `/pl/kontakt` is prerendered and the site layout revalidates every five
   * minutes, so the list handed in as a prop can be offering an hour that went
   * four minutes ago. Asking once on mount costs one round trip and is the
   * difference between "pick another" after a failed booking and never showing
   * the dead hour at all.
   *
   * It starts as the prop so the first paint has a month to draw. A failed
   * refresh keeps it: an empty calendar because the network blinked would be a
   * worse answer than a slightly stale one, and the booking re-derives the
   * list a third time before it writes anything.
   */
  const [days, setDays] = useState<readonly MeetingDay[]>(meetingDays)

  useEffect(() => {
    let live = true
    void meetingDaysAction('DISCOVERY').then((fresh) => {
      if (live && fresh.length > 0) setDays(fresh)
    })
    return () => {
      live = false
    }
  }, [])

  /** The times one day offers — `ConsultationCalendar` asks per day. */
  const slotsFor = (dayISO: string): readonly string[] =>
    days.find((day) => day.date === dayISO)?.times ?? []
  const openDays = days.map((day) => day.date)

  /**
   * Five checks, written out.
   *
   * This was a zod schema, and zod is 300 KB of parser for "is this string
   * empty" — the only thing on the home page that pulled it into the browser,
   * where Lighthouse measured 62 KB transferred and most of it unused. The
   * SERVER still validates with a schema (`submitLeadAction`), which is the
   * copy that decides whether a lead is written; this one only decides which
   * sentence appears under which field.
   */
  const v = copy.validation
  const focusError = formState.focus.length > 0 ? undefined : v.focusRequired
  const detailsError = formState.details.trim().length > 0 ? undefined : v.detailsRequired
  const nameError = formState.name.trim().length >= 2 ? undefined : v.nameRequired
  const emailError = emailProblem(formState.email.trim(), v)
  const linkError =
    formState.link.trim().length === 0 || LINK_REGEX.test(formState.link.trim())
      ? undefined
      : v.linkInvalid

  const focusResult = { success: !focusError }
  const detailsResult = { success: !detailsError }
  const contactResult = { success: !nameError && !emailError && !linkError }

  const profileError = formState.profile ? undefined : copy.validation.profileRequired
  const channelError = formState.channel ? undefined : copy.validation.channelRequired
  const matterError = formState.matter ? undefined : copy.validation.matterRequired
  const slotError = formState.slot ? undefined : copy.validation.slotRequired

  const meeting = isMeeting(formState.channel)

  /**
   * Which answer unlocks which step — and the two paths are genuinely different
   * lengths, not one path with skipped screens.
   *
   * A MESSAGE asks four things: how, what it is about, the description, the
   * contact details. It does not ask whether you are a brand or a creator, and
   * it does not ask which service area you need — a complaint about an invoice
   * has no service area, and making somebody pick one to file it is the kind of
   * form that gets abandoned.
   *
   * A MEETING keeps the full brief, because fifteen minutes with somebody is
   * worth preparing for: who they are, the area, the problem, the details and
   * a slot.
   *
   * The steps that do not apply are NOT RENDERED, so `Children.toArray` gives
   * the stepper a real count — four or six — rather than six with two waved
   * through.
   */
  const isStepValid = (() => {
    if (activeStep === 1) return Boolean(formState.channel)
    if (meeting) {
      if (activeStep === 2) return Boolean(formState.profile)
      if (activeStep === 3) return focusResult.success
      if (activeStep === 4) return detailsResult.success
      if (activeStep === 5) return contactResult.success
      if (activeStep === 6) return Boolean(formState.slot)
      return true
    }
    if (activeStep === 2) return Boolean(formState.matter)
    if (activeStep === 3) return detailsResult.success
    if (activeStep === 4) return contactResult.success
    return true
  })()

  const handleChange =
    (field: 'details' | 'name' | 'email' | 'link') =>
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value = event.target.value
      setFormState((prev) => ({ ...prev, [field]: value }))
    }

  const handleSelect = (value: string) => {
    setFormState((prev) => {
      const isSelected = prev.focus.includes(value)
      const nextFocus = isSelected
        ? prev.focus.filter((item) => item !== value)
        : [...prev.focus, value]
      return { ...prev, focus: nextFocus }
    })
  }

  /**
   * Send the enquiry.
   *
   * Until this existed, finishing the form only flipped `submitted` and drew
   * the thank-you panel — five steps of answers went nowhere, while the `lead`
   * table they belong in already existed and the staff panel already listed it.
   *
   * A failed send does NOT show the success panel. Telling somebody we have
   * their enquiry when we do not is the one outcome worth guarding against, so
   * the error surfaces and the form stays where it is, still filled in.
   */
  const submitEnquiry = async () => {
    setSending(true)

    /**
     * The meeting is BOOKED FIRST, and the enquiry only if it worked.
     *
     * Order matters and this is the safe one. A lead written before the slot is
     * claimed can end up describing a term somebody else took a second earlier
     * — the desk then reads „spotkanie 14:00" against a calendar that says
     * otherwise, which is the confusion the whole change exists to remove. The
     * other way round, a failed lead leaves a real reservation with a real
     * confirmation and no note attached, which is recoverable by a human.
     */
    let bookingId: string | null = null
    if (meeting && formState.slot) {
      const whole = formState.name.trim()
      const cut = whole.indexOf(' ')
      const booked = await bookMeetingAction({
        firstName: cut > 0 ? whole.slice(0, cut) : whole,
        ...(cut > 0 ? { lastName: whole.slice(cut + 1).trim() } : {}),
        email: formState.email.trim(),
        place: formState.channel === 'stacjonarnie' ? 'OFFICE' : 'ONLINE',
        date: formState.slot.date,
        time: formState.slot.time,
      })

      if (!booked.ok) {
        setSending(false)
        // A slot that went while the form was open is not an error the reader
        // caused, so the list is refreshed and the choice cleared rather than
        // leaving them pressing send on an hour that no longer exists.
        if (booked.taken) {
          const fresh = await meetingDaysAction('DISCOVERY')
          setDays(fresh)
          setFormState((prev) => ({ ...prev, slot: null }))
        }
        toast({ kind: 'error', title: 'Termin niedostępny', description: booked.error })
        return
      }
      bookingId = booked.id
    }

    const result = await submitLeadAction({
      // A booked meeting makes it a consultation; anything else is a plain
      // enquiry. Asked of the booking, not of the slot: going back and
      // switching to a message leaves the chosen hour in the state.
      kind: bookingId ? 'CONSULTATION' : 'CONTACT',
      name: formState.name.trim(),
      email: formState.email.trim(),
      // A message names its own subject — that is what step 2 asked for, and it
      // is what routes it to a desk. A meeting has no such answer, so it falls
      // back to who booked it.
      subject:
        formState.matter ??
        (formState.profile === 'firma' ? 'Zapytanie od firmy' : 'Zapytanie od twórcy'),
      message: formState.details.trim(),
      payload: {
        profile: formState.profile,
        audience:
          formState.profile === 'tworca' ? (AUDIENCE_STEPS[formState.audience] ?? null) : null,
        channel: formState.channel,
        matter: formState.matter,
        // Kept beside `channel` and NULL for a message: the desk's screens and
        // every lead written before this step existed read `format`, and a
        // message has no format to report.
        format: meeting ? formState.channel : null,
        focus: formState.focus,
        link: formState.link.trim() || null,
        slot: meeting ? formState.slot : null,
        // The reservation this enquiry belongs to, so the desk can open one
        // from the other. NULL for a message, which books nothing.
        bookingId,
      },
      sourcePath: typeof window === 'undefined' ? undefined : window.location.pathname,
      acceptedPrivacy: true,
    })
    setSending(false)

    if (!result.ok) {
      toast({ kind: 'error', title: 'Nie udało się wysłać', description: result.error })
      return
    }
    setSubmitted(true)
    toast({
      kind: 'success',
      title: meeting ? 'Termin zarezerwowany' : 'Wiadomość wysłana',
      description: meeting
        ? 'Rezerwacja jest w naszym kalendarzu. Potwierdzenie poszło na podany adres.'
        : 'Odezwiemy się na podany adres e-mail.',
    })
  }

  /**
   * What the reader checks before pressing send.
   *
   * Rendered at the end of whichever step is LAST on their branch — step 5 for
   * a message, step 6 for a meeting — so nobody sends a form whose first answer
   * they gave four screens ago and can no longer see.
   */
  const recap = (
    <p className="rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-3 text-[0.8rem] text-white/70">
      {formState.channel === 'mail' ? 'Wiadomość' : null}
      {formState.channel === 'online' ? 'Rozmowa online, 30 min' : null}
      {formState.channel === 'stacjonarnie' ? 'Rozmowa u nas, 30 min' : null}
      {formState.matter ? ` · ${formState.matter}` : null}
      {formState.profile === 'firma' ? ' · firma / marka' : null}
      {formState.profile === 'tworca' ? ` · twórca · ${AUDIENCE_STEPS[formState.audience]}` : null}
    </p>
  )

  return (
    <section
      id={id}
      className="contact-stepper-shell relative w-full scroll-mt-24 overflow-hidden rounded-[2rem]"
    >
      {/* The light this section never had.
          Every other band on the site has something behind its type — the
          bento's bloom, the `FLOU` ghost, the numbers grid — and the contact
          page closed on flat black with a grey box on it. Two pools rather than
          one: a warm one up under the choices and a cooler one low and left, so
          the band has a direction instead of a single glow in the middle.
          Sized to the section, faded out well inside its own box, so there is
          no edge to see. */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse 52% 46% at 72% 18%, oklch(0.66 0.25 320 / 0.16) 0%, oklch(0.62 0.24 290 / 0.07) 46%, transparent 72%),' +
            'radial-gradient(ellipse 46% 42% at 12% 82%, oklch(0.72 0.22 350 / 0.1) 0%, transparent 68%)',
        }}
      />

      {/* No `CONTAINER`, no vertical band padding: this is a column in the contact
          page's grid now, not a band of its own. Its measure and the air above
          it belong to the page, which is what keeps the form and the office
          panel on one top edge. */}
      <div className="relative">
        {/* NO SHORTCUT CARDS ABOVE THE FORM. There were two — „Nie wiesz,
            czego potrzebujesz?" and the mailto tile — and the owner took them
            out with the wordmark when this page became one screen. Both said
            something the form itself says: its first step offers „Napisz
            wiadomość" as one of three channels, and the address is in the
            panel beside it. What they cost was 128px at the top of the one
            page on the site where the content has to fit under the fold. */}
        <Reveal index={1}>
          <Stepper
            onStepChange={(step) => {
              setActiveStep(step)
              if (submitted) setSubmitted(false)
            }}
            onFinalStepCompleted={() => {
              void submitEnquiry()
            }}
            backButtonText={copy.buttons.back}
            nextButtonText={copy.buttons.next}
            // Two endings, two verbs. "Umów konsultację" on a form that books
            // nothing is the kind of small lie that makes people hesitate.
            completeButtonText={meeting ? copy.buttons.submit : copy.buttons.submitMail}
            // The ONLY gate between an incomplete answer and the next step.
            // The numbered circles used to route around it — they called
            // updateStep() directly, so you could land on the booking step with
            // the whole form empty. They are decoration now (see the stepper's
            // docblock) and navigation is Wstecz/Dalej, which respect this.
            nextButtonProps={{ disabled: !isStepValid || sending }}
            completedContent={
              <div className="step-default pb-6">
                <div className="rounded-2xl border border-foreground/10 bg-foreground/5 p-6 text-sm">
                  <p className="font-semibold text-foreground">{copy.success.title}</p>
                  <p className="mt-2 text-sm text-muted-foreground">
                    {meeting ? copy.success.description : copy.success.descriptionMail}
                  </p>
                </div>
              </div>
            }
            stepCircleContainerClassName="contact-stepper-card"
            stepContainerClassName="contact-stepper-row"
            contentClassName="contact-stepper-content"
            footerClassName="contact-stepper-footer"
          >
            {/* ── 1 · HOW ────────────────────────────────────────
                FIRST, because it decides what the rest of the form asks and how
                long it is. See `Channel`. */}
            <Step>
              <div className="flex flex-col gap-[clamp(0.6rem,1.6svh,1rem)]">
                <StepHead title={copy.steps.channel.title} hint={copy.steps.channel.hint} />
                <div className="grid gap-3 sm:grid-cols-3">
                  <ChoiceTile
                    label="Napisz wiadomość"
                    hint="Opisz sprawę, odpiszemy na maila."
                    Icon={Mail}
                    tone="var(--flou-pink)"
                    active={formState.channel === 'mail'}
                    onClick={() => setFormState((prev) => ({ ...prev, channel: 'mail' }))}
                  />
                  <ChoiceTile
                    label="Rozmowa online"
                    hint="Wideorozmowa, link wyślemy mailem."
                    Icon={Monitor}
                    tone="var(--flou-magenta)"
                    active={formState.channel === 'online'}
                    onClick={() => setFormState((prev) => ({ ...prev, channel: 'online' }))}
                  />
                  <ChoiceTile
                    label="Rozmowa u nas"
                    hint="Pod Wrocławiem, kawa i ekran na miejscu."
                    Icon={MapPin}
                    tone="var(--flou-violet)"
                    active={formState.channel === 'stacjonarnie'}
                    onClick={() => setFormState((prev) => ({ ...prev, channel: 'stacjonarnie' }))}
                  />
                </div>
                {channelError ? (
                  <p className="text-xs text-destructive/80" role="alert">
                    {channelError}
                  </p>
                ) : null}
              </div>
            </Step>

            {/* ── 2 · WHO, or WHAT IT IS ABOUT ───────────────────
                A meeting is worth preparing for, so it asks who is coming. A
                message asks what the message is about, which is what decides
                whose desk it lands on. */}
            {meeting ? (
              <Step>
                <div className="flex flex-col gap-[clamp(0.6rem,1.6svh,1rem)]">
                  <StepHead title={copy.steps.profile.title} hint={copy.steps.profile.hint} />
                  <div className="grid gap-3 sm:grid-cols-2">
                    <ChoiceTile
                      label="Firma / marka"
                      hint="Kampanie, content produktowy, obsługa marki."
                      Icon={Building2}
                      tone="var(--flou-magenta)"
                      active={formState.profile === 'firma'}
                      onClick={() => setFormState((prev) => ({ ...prev, profile: 'firma' }))}
                    />
                    <ChoiceTile
                      label="Twórca"
                      hint="Kanał YouTube, social media, budowa zasięgu."
                      Icon={Users}
                      tone="var(--flou-violet)"
                      active={formState.profile === 'tworca'}
                      onClick={() => setFormState((prev) => ({ ...prev, profile: 'tworca' }))}
                    />
                  </div>

                  {/* Only creators get the audience question — a company's head
                      count says nothing about the work. */}
                  {formState.profile === 'tworca' ? (
                    <div className="rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-4">
                      <AudienceSlider
                        value={formState.audience}
                        onChange={(audience) => setFormState((prev) => ({ ...prev, audience }))}
                      />
                    </div>
                  ) : null}

                  {profileError ? (
                    <p className="text-xs text-destructive/80" role="alert">
                      {profileError}
                    </p>
                  ) : null}
                </div>
              </Step>
            ) : (
              <Step>
                <div className="flex flex-col gap-[clamp(0.6rem,1.6svh,1rem)]">
                  <StepHead title={copy.steps.matter.title} hint={copy.steps.matter.hint} />
                  <div className="grid gap-3 sm:grid-cols-2">
                    {copy.steps.matter.options.map((option) => (
                      <ChoiceTile
                        key={option}
                        label={option}
                        active={formState.matter === option}
                        onClick={() => setFormState((prev) => ({ ...prev, matter: option }))}
                      />
                    ))}
                  </div>
                  {matterError ? (
                    <p className="text-xs text-destructive/80" role="alert">
                      {matterError}
                    </p>
                  ) : null}
                </div>
              </Step>
            )}

            {/* ── 3 · AREA ───────────────────────────────────────
                Meetings only. "Wybierz obszar" has no answer for a complaint
                about an invoice, and making somebody pick one to file it is how
                a form gets abandoned. */}
            {meeting ? (
              <Step>
                <div className="flex flex-col gap-[clamp(0.6rem,1.6svh,1rem)]">
                  <StepHead title={copy.steps.first.title} hint={copy.steps.first.hint} />
                  <div className="grid gap-3 sm:grid-cols-2">
                    {copy.steps.first.options.map((option) => (
                      <ChoiceTile
                        key={option}
                        label={option}
                        active={formState.focus.includes(option)}
                        onClick={() => handleSelect(option)}
                      />
                    ))}
                  </div>
                  {focusError ? (
                    <p className="text-xs text-destructive/80" role="alert">
                      {focusError}
                    </p>
                  ) : null}
                </div>
              </Step>
            ) : null}

            {/* ── 4 · DETAILS ────────────────────────────────── */}
            <Step>
              <div className="flex flex-col gap-[clamp(0.6rem,1.6svh,1rem)]">
                <StepHead
                  title={meeting ? copy.steps.second.title : 'Opisz sprawę'}
                  hint={
                    meeting
                      ? copy.steps.second.hint
                      : 'Im konkretniej, tym szybciej odpowiemy na temat.'
                  }
                />
                <label className="flex flex-col gap-2 text-sm text-white/80">
                  <span>{copy.steps.second.label}</span>
                  <textarea
                    rows={5}
                    value={formState.details}
                    onChange={handleChange('details')}
                    placeholder={copy.steps.second.placeholder}
                    aria-invalid={Boolean(detailsError)}
                    aria-describedby={detailsError ? 'contact-details-error' : undefined}
                    className="min-h-[140px] resize-y rounded-2xl border border-white/12 bg-black/30 px-4 py-3 text-[16px] text-white outline-none transition focus:border-white/35 sm:text-sm"
                  />
                </label>
                {detailsError ? (
                  <p
                    id="contact-details-error"
                    className="text-xs text-destructive/80"
                    role="alert"
                  >
                    {detailsError}
                  </p>
                ) : null}
              </div>
            </Step>

            {/* ── 5 · CONTACT ────────────────────────────────── */}
            <Step>
              <div className="flex flex-col gap-[clamp(0.6rem,1.6svh,1rem)]">
                <StepHead title={copy.steps.third.title} hint={copy.steps.third.hint} />
                {/* ONE COLUMN, and it is the bent band that decides it.
                    Name and e-mail shared a `sm:grid-cols-2` row, which put a
                    46px rectangle beside a 59px pill with a gradient chip on
                    it — two controls asking for the same kind of thing, in one
                    row, agreeing on neither height nor shape. That reads as a
                    rendering fault rather than as emphasis.
                    Stacking is also what the rest of this step already does:
                    the channel field and the message under it have always run
                    the full width, so the pair was the odd row, not the fix.
                    The closing form on the home page was stacked for the same
                    reason. */}
                <div className="grid gap-4">
                  <label className="flex flex-col gap-2 text-sm text-white/80">
                    <span>{copy.fields.nameLabel}</span>
                    <input
                      type="text"
                      value={formState.name}
                      onChange={handleChange('name')}
                      placeholder={copy.fields.namePlaceholder}
                      aria-invalid={Boolean(nameError)}
                      aria-describedby={nameError ? 'contact-name-error' : undefined}
                      className="rounded-2xl border border-white/12 bg-black/30 px-4 py-3 text-[16px] text-white outline-none transition focus:border-white/35 sm:text-sm"
                    />
                    {nameError ? (
                      <span
                        id="contact-name-error"
                        className="text-xs text-destructive/80"
                        role="alert"
                      >
                        {nameError}
                      </span>
                    ) : null}
                  </label>
                  {/* The one label on this step carrying `htmlFor`. Wrapping
                      is still what associates the other three, and it would
                      have worked here too — `CurvedInput` renders a real
                      `<input type="email">`, not React Bits' shadow field. But
                      a linter cannot see through a component boundary to prove
                      that, and an explicit pair is the stronger association
                      anyway. */}
                  <label
                    htmlFor="contact-email"
                    className="flex flex-col gap-2 text-sm text-white/80"
                  >
                    <span>{copy.fields.emailLabel}</span>
                    {/* The bent band, same component and same settings as the
                        home page's closing form — owner's pick for "the mail
                        one", and this is the site's other place that asks for
                        one. It stands 13px taller than the plain fields, which
                        is fine now that it has a row to itself and nothing to
                        be compared against edge to edge. */}
                    <CurvedInput
                      id="contact-email"
                      type="email"
                      value={formState.email}
                      onChange={handleChange('email')}
                      placeholder={copy.fields.emailPlaceholder}
                      autoComplete="email"
                      invalid={Boolean(emailError)}
                      aria-describedby={emailError ? 'contact-email-error' : undefined}
                    />
                    {emailError ? (
                      <span
                        id="contact-email-error"
                        className="text-xs text-destructive/80"
                        role="alert"
                      >
                        {emailError}
                      </span>
                    ) : null}
                  </label>
                </div>
                <label className="flex flex-col gap-2 text-sm text-white/80">
                  <span>{copy.fields.linkLabel}</span>
                  <input
                    type="text"
                    value={formState.link}
                    onChange={handleChange('link')}
                    placeholder={copy.fields.linkPlaceholder}
                    aria-invalid={Boolean(linkError)}
                    aria-describedby={linkError ? 'contact-link-error' : undefined}
                    className="rounded-2xl border border-white/12 bg-black/30 px-4 py-3 text-[16px] text-white outline-none transition focus:border-white/35 sm:text-sm"
                  />
                  {linkError ? (
                    <span
                      id="contact-link-error"
                      className="text-xs text-destructive/80"
                      role="alert"
                    >
                      {linkError}
                    </span>
                  ) : null}
                </label>

                {/* The last screen on the message branch, so the summary belongs
                    here too — see `recap`. */}
                {meeting ? null : recap}
              </div>
            </Step>

            {/* ── 6 · WHEN ───────────────────────────────────────
                Meetings only, and genuinely absent otherwise. The online /
                in-person choice was made in step 1, which is why nothing here
                asks for it again. */}
            {meeting ? (
              <Step>
                <div className="flex flex-col gap-5">
                  <StepHead title={copy.steps.fourth.title} hint={copy.steps.fourth.hint} />
                  <ConsultationCalendar
                    value={formState.slot}
                    onChange={(slot) => setFormState((prev) => ({ ...prev, slot }))}
                    slots={slotsFor}
                    openDays={openDays}
                    note="Wolne terminy z kalendarza FLOU, w godzinach otwarcia biura."
                    reservationNote="Rezerwujemy ten termin od razu — online i u nas to jeden kalendarz, więc znika z obu."
                  />
                  {openDays.length === 0 ? (
                    <p className="text-xs text-white/55">
                      Kalendarz jest w tej chwili pełny. Napisz wiadomość — odezwiemy się z
                      propozycją terminu.
                    </p>
                  ) : null}
                  {slotError ? (
                    <p className="text-xs text-destructive/80" role="alert">
                      {slotError}
                    </p>
                  ) : null}
                  {recap}
                </div>
              </Step>
            ) : null}
          </Stepper>
        </Reveal>
      </div>
    </section>
  )
}
